import {appliedActionContent, ApplyActionsResult, enrichActionData, summarizeAppliedAction} from "@buildwithflux/core";
import {
    CopilotActionKey,
    ICopilotActions,
    actionMessageAppliedSummaries,
    actionMessageApplyingSummaries,
    makeActionGenerationMessage,
} from "@buildwithflux/models";
import {uuid} from "short-uuid";

import {createSortedThreadMessagesSelector} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/selectors/createSortedThreadMessagesSelector";
import {
    CopilotChatStoreGetter,
    CopilotChatStoreSetter,
    ICopilotChatStoreDependencies,
} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/state/useCopilotChatStore";
import {createCopilotActions} from "modules/document/state/actions/copilot";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

async function createAndPersistActionGenerationMessage(
    threadUid: string,
    messageUid: string,
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    dependencies: ICopilotChatStoreDependencies,
) {
    const {logger, chatRepository} = dependencies;

    const {currentDocumentUid, threads} = get();
    if (!currentDocumentUid) return;

    const thread = threads[threadUid];
    if (!thread) return;

    const actionMessage = thread.messages[messageUid];
    if (!actionMessage) return;

    const actionGenerationMessageUid = uuid();

    // Get the last message in the thread for the reply chain
    const currentBranchMessages = createSortedThreadMessagesSelector(threadUid)(get());
    const replyToMessageUid = currentBranchMessages[currentBranchMessages.length - 1]?.uid || "root";

    // the action_generation message is always replying to (i.e. linked to)
    // the latest message.
    const actionGenerationMessage = makeActionGenerationMessage({
        uid: actionGenerationMessageUid,
        belongs_to_thread_uid: threadUid,
        reply_to_message_uid: replyToMessageUid,
    });

    set((draft) => {
        const draftThread = draft.threads[threadUid];
        if (draftThread) {
            draftThread.updated_at = actionGenerationMessage.updated_at;
            draftThread.messages[actionGenerationMessageUid] = actionGenerationMessage;
        }
    });

    // First, create the "action_generation" message, in UI this renders as
    // an indicator that copilot is applying changes.
    try {
        await chatRepository.createMessage(currentDocumentUid, threadUid, actionGenerationMessage);

        return actionGenerationMessage;
    } catch (error) {
        if (logger) {
            logger.error("Failed to create action generation message:", error);
        }
        FluxLogger.captureError(error);

        set((draft) => {
            const draftThread = draft.threads[threadUid];
            if (draftThread) {
                delete draftThread.messages[actionGenerationMessageUid];
            }
        });

        return undefined;
    }
}

/**
 * Executes copilot message actions and updates the store with the results.
 * This action is responsible for fetching actions from the backend, applying them,
 * and updating the message status.
 */
export function executeMessageActions(
    set: CopilotChatStoreSetter,
    get: CopilotChatStoreGetter,
    dependencies: ICopilotChatStoreDependencies,
) {
    return async (threadUid: string, messageUid: string): Promise<ApplyActionsResult | undefined> => {
        const {logger, functionsAdapter, documentStore} = dependencies;

        const {currentDocumentUid, threads} = get();
        if (!currentDocumentUid) return;

        const thread = threads[threadUid];
        if (!thread) return;

        const message = thread.messages[messageUid];
        if (!message) return;

        await get().setActionExecuting(threadUid, messageUid);

        const {chatRepository, currentUserService} = dependencies;
        const currentUser = currentUserService.getCurrentUser();
        if (!currentUser) {
            throw new Error("Current user not found");
        }

        const document = documentStore.getState().document;
        if (!document) {
            throw new Error("Document not found");
        }

        const actionGenerationMessage = await createAndPersistActionGenerationMessage(
            threadUid,
            messageUid,
            set,
            get,
            dependencies,
        );
        if (!actionGenerationMessage) throw new Error("Failed to create action generation message");

        let actions: ICopilotActions | undefined;

        // Then, call backend to generate actions based on the message
        try {
            const responseData = await functionsAdapter.triggerCopilotActionGeneration({
                documentUid: document.uid,
                threadUid,
                messageUid,
            });

            actions = responseData.data;

            actionGenerationMessage.meta_data.status = "ready_to_apply";
            await chatRepository.setMessage(currentDocumentUid, threadUid, {
                uid: actionGenerationMessage.uid,
                meta_data: {
                    ...actionGenerationMessage.meta_data,
                },
                updated_at: Date.now(),
            });
        } catch (error) {
            if (logger) {
                logger.error("Error triggering Copilot Action Generation:", error);
            }
            FluxLogger.captureError(error);

            try {
                await chatRepository.setMessage(currentDocumentUid, threadUid, {
                    uid: actionGenerationMessage.uid,
                    meta_data: {
                        type: "action_generation",
                        status: "error",
                        reason: "Fail to generate actions",
                    },
                    updated_at: Date.now(),
                });
            } catch (updateError) {
                if (logger) {
                    logger.error("Failed to update action generation message on error:", updateError);
                }
                FluxLogger.captureError(updateError);
            }

            await get().resetActionExecution(threadUid, messageUid);

            return undefined;
        }

        const actionName = Object.keys(actions)[0] as CopilotActionKey;
        let applyResult: ApplyActionsResult | undefined;
        try {
            // When actions are returned, apply them.

            // Mark action message as "applying" with appropriate content summary
            const timestamp = Date.now();
            await chatRepository.setMessage(document.uid, threadUid, {
                uid: actionGenerationMessage.uid,
                updated_at: timestamp,
                meta_data: {
                    ...actionGenerationMessage.meta_data,
                    status: "applying",
                    contentSummary: actionMessageApplyingSummaries[actionName],
                },
            });
            await chatRepository.setThread(document.uid, {uid: threadUid, updated_at: timestamp});

            const {actions: enrichedActions, actionContext} = await enrichActionData(actions, dependencies);

            // Use DocumentStore to dispatch copilot actions directly to Zustand
            const copilotActions = createCopilotActions(dependencies.documentStore);
            copilotActions.applyCopilotActions({
                actions: enrichedActions,
                ...actionContext,
                userUid: dependencies.currentUserService.getCurrentUser()?.uid ?? "",
                resultCallback: (result) => (applyResult = result),
            });
            if (!applyResult) {
                // Should never happen
                throw new Error("applyActions did not produce a result");
            }

            // Get the updated document after the dispatch so appliedActionContent can access the new elements
            const updatedDocument = documentStore.getState().document;
            if (!updatedDocument) {
                throw new Error("Updated document not found");
            }

            const summary = summarizeAppliedAction(actionName, applyResult.appliedActionData);
            await chatRepository.setMessage(document.uid, threadUid, {
                uid: actionGenerationMessage.uid,
                meta_data: {
                    ...actionGenerationMessage.meta_data,
                    status: "completed",
                    contentSummary: summary || actionMessageAppliedSummaries.empty,
                },
                content: appliedActionContent(actionName, applyResult.appliedActionData, updatedDocument),
                updated_at: timestamp,
            });
            await chatRepository.setThread(document.uid, {uid: threadUid, updated_at: timestamp});
        } catch (err) {
            logger.error("Failed to apply actions", err);
            FluxLogger.captureError(err);
            const timestamp = Date.now();
            await chatRepository.setMessage(document.uid, threadUid, {
                uid: actionGenerationMessage.uid,
                meta_data: {
                    ...actionGenerationMessage.meta_data,
                    status: "completed",
                    contentSummary: actionMessageAppliedSummaries.empty,
                },
                content: "",
                updated_at: timestamp,
            });
            await chatRepository.setThread(document.uid, {uid: threadUid, updated_at: timestamp});
        }

        await get().setActionCompleted(threadUid, messageUid);

        return applyResult;
    };
}
